import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  collection,
  query,
  orderBy,
  getDocs,
  doc,
  getDoc,
  setDoc,
  addDoc,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { addTimelineEvent } from "../services/timelineService";
import {
  UserPlus,
  UserMinus,
  FileText,
  FileMinus,
  StickyNote,
  Gavel,
  DollarSign,
  Clock,
  Users,
  Edit3,
  X,
  ChevronRight,
  CalendarCheck,
  CheckCircle2,
  XCircle,
  ArrowRightLeft,
  MessageSquare,
  RefreshCw,
  Send,
  AlertCircle,
  MapPin,
  ChevronDown,
} from "lucide-react";

// ─── Config ───

const EVENT_CONFIG = {
  case_shared: { icon: UserPlus, color: "#22c55e", label: "Case Shared" },
  case_unshared: { icon: UserMinus, color: "#ef4444", label: "Case Unshared" },
  client_shared: { icon: UserPlus, color: "#3b82f6", label: "Shared with Client" },
  client_unshared: { icon: UserMinus, color: "#f97316", label: "Client Access Removed" },
  case_updated: { icon: Edit3, color: "#3b82f6", label: "Case Updated" },
  hearing_updated: { icon: Clock, color: "#a855f7", label: "Hearing Updated" },
  hearing_status: { icon: CalendarCheck, color: "#D19B15", label: "Hearing Status" },
  hearing_not_filled: { icon: AlertCircle, color: "#f59e0b", label: "Hearing Status Not Filled" },
  notes_updated: { icon: StickyNote, color: "#eab308", label: "Notes Updated" },
  file_added: { icon: FileText, color: "#22c55e", label: "File Added" },
  file_removed: { icon: FileMinus, color: "#ef4444", label: "File Removed" },
  case_disposed: { icon: Gavel, color: "#ef4444", label: "Case Disposed" },
  expense_added: { icon: DollarSign, color: "#22c55e", label: "Expense Added" },
  unknown: { icon: Users, color: "#6b7280", label: "Activity" },
};

const STATUS_OPTIONS = [
  { value: "attended", label: "Attended", icon: CheckCircle2, color: "#22c55e", bg: "bg-green-500/10 border-green-500/30 text-green-400" },
  { value: "not_attended", label: "Not Attended", icon: XCircle, color: "#ef4444", bg: "bg-red-500/10 border-red-500/30 text-red-400" },
  { value: "postponed", label: "Postponed", icon: ArrowRightLeft, color: "#f59e0b", bg: "bg-yellow-500/10 border-yellow-500/30 text-yellow-400" },
];

// ─── Helpers ───

function parseDDMMYYYY(dateStr) {
  if (!dateStr) return null;
  const [day, month, year] = dateStr.split("/").map(Number);
  if (!day || !month || !year) return null;
  return new Date(year, month - 1, day);
}

function isHearingDateReached(nextHearingDate) {
  const hearingDate = parseDDMMYYYY(nextHearingDate);
  if (!hearingDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  hearingDate.setHours(0, 0, 0, 0);
  return today >= hearingDate;
}

function formatTimestamp(timestamp) {
  if (!timestamp) return "";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

// "08/07/2026" → "08 Jul 2026 (Wednesday)"
function formatHearingDateLong(dateStr) {
  const d = parseDDMMYYYY(dateStr);
  if (!d) return dateStr || "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleDateString("en-IN", { month: "short" });
  const weekday = d.toLocaleDateString("en-IN", { weekday: "long" });
  return `${day} ${month} ${d.getFullYear()} (${weekday})`;
}

function initialsOf(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean).slice(0, 2);
  return parts.map((p) => p[0]).join("").toUpperCase() || "?";
}

function getRelativeTime(timestamp) {
  if (!timestamp) return "";
  const now = new Date();
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const diff = now - date;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (seconds < 60) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

// ─── Two-Way Notes (reusable) ───

// `externalComposer` is for the pending-hearing card, where the mockup puts a
// plain textarea with a character counter in the right column and a single
// "Save Hearing" button in the card footer — so the composer has no send button
// of its own and the parent drives submit/reset through `apiRef`.
function HearingNotes({
  caseId,
  caseCollection,
  hearingDocId,
  onAfterSave,
  saveDisabled,
  hideHeader = false,
  refreshKey = 0,
  externalComposer = false,
  apiRef,
  maxLength = 1000,
}) {
  const { globalUser, globalData } = useAuth();
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [sendingNote, setSendingNote] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const notesEndRef = useRef(null);

  const fetchNotes = async (isRefresh = false) => {
    if (!caseId || !caseCollection || !hearingDocId) { setLoading(false); return; }
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      const notesRef = collection(db, caseCollection, caseId, "hearingNotes", hearingDocId, "notes");
      const q = query(notesRef, orderBy("timestamp", "asc"));
      const snap = await getDocs(q);
      setNotes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error fetching hearing notes:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [caseId, caseCollection, hearingDocId, refreshKey]);

  useEffect(() => {
    notesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [notes]);

  const handleSendNote = async () => {
    if (!hearingDocId) return;
    if (!noteText.trim() && !onAfterSave) return;
    setSendingNote(true);
    try {
      if (noteText.trim()) {
        const hearingRef = doc(db, caseCollection, caseId, "hearingNotes", hearingDocId);
        await setDoc(hearingRef, { hearingDate: hearingDocId.replace(/-/g, "/") }, { merge: true });

        const notesRef = collection(db, caseCollection, caseId, "hearingNotes", hearingDocId, "notes");
        const newNote = {
          text: noteText.trim(),
          authorId: globalUser?.uid || "",
          authorName: globalData?.name || globalUser?.displayName || "Unknown",
          timestamp: serverTimestamp(),
        };
        const docRef = await addDoc(notesRef, newNote);
        setNotes((prev) => [...prev, { id: docRef.id, ...newNote, timestamp: new Date() }]);
        setNoteText("");
      }
      if (onAfterSave) await onAfterSave();
    } catch (err) {
      console.error("Error sending note:", err);
    } finally {
      setSendingNote(false);
    }
  };

  // Let the card footer's "Save Hearing" button drive this composer.
  if (apiRef) {
    apiRef.current = { submit: handleSendNote, reset: () => setNoteText("") };
  }

  return (
    <div>
      {!hideHeader && (
        <div className="flex items-center justify-between mb-2">
          <p className="text-gray-400 text-xs uppercase tracking-wide font-medium">Hearing Notes</p>
          <button
            onClick={() => fetchNotes(true)}
            disabled={refreshing}
            className="text-gray-400 hover:text-white transition-colors p-1 disabled:opacity-50"
            title="Refresh Notes"
          >
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
          </button>
        </div>
      )}
      <div className={`max-h-[200px] overflow-y-auto space-y-2 ${notes.length > 0 ? "mb-3" : ""}`}>
        {loading && (
          <div className="text-center py-4">
            <div className="w-5 h-5 border-2 border-gray-600 border-t-blue-400 rounded-full animate-spin inline-block" />
          </div>
        )}
        {!loading && notes.map((note) => {
          const isMine = note.authorId === globalUser?.uid;
          return (
            <div key={note.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-2 ${isMine ? "bg-blue-500/10 border border-blue-500/20" : "bg-gray-800 border border-gray-700"}`}>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className={`text-[10px] font-semibold ${isMine ? "text-blue-400" : "text-gray-400"}`}>{note.authorName || "Unknown"}</span>
                  <span className="text-[10px] text-gray-600">{note.timestamp ? getRelativeTime(note.timestamp) : ""}</span>
                </div>
                <p className="text-gray-200 text-sm leading-relaxed">{note.text}</p>
              </div>
            </div>
          );
        })}
        <div ref={notesEndRef} />
      </div>
      {externalComposer ? (
        <div className="relative">
          <textarea
            value={noteText}
            maxLength={maxLength}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Add hearing notes or observations..."
            rows={5}
            className="w-full resize-none rounded-xl border border-slate-200 bg-white px-3.5 py-3 pb-8 text-sm text-slate-800 placeholder-slate-400 focus:border-[#C99F4A] focus:outline-none focus:ring-2 focus:ring-[#C99F4A]/25"
          />
          <span className="pointer-events-none absolute bottom-3 right-3.5 text-xs text-slate-400">
            {noteText.length} / {maxLength}
          </span>
        </div>
      ) : (
      <div className="flex items-end gap-2 w-full">
        <textarea
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSendNote();
            }
          }}
          placeholder="Add a note..."
          rows={1}
          className="flex-1 px-3 py-2 rounded-lg bg-[#0f0f23] text-white text-xs border border-blue-900/40 focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-600 resize-none"
        />
        <button
          onClick={handleSendNote}
          disabled={sendingNote || (!noteText.trim() && !onAfterSave) || saveDisabled}
          className="p-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center shrink-0"
        >
          {sendingNote ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Send size={16} />
          )}
        </button>
      </div>
      )}
    </div>
  );
}

// ─── Detail Modal (with notes for hearing_status events) ───

function TimelineDetailModal({ event, onClose, caseId, caseCollection }) {
  if (!event) return null;

  const config = EVENT_CONFIG[event.type] || EVENT_CONFIG.unknown;
  const hasChanges = event.changes && Object.keys(event.changes).length > 0;
  const isHearingEvent = event.type === "hearing_status";
  const hearingDocId = event.metadata?.hearingDocId || null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[1100] p-4 pt-20">
      <div className="bg-[#1a1a2e] rounded-xl max-w-2xl w-full shadow-2xl border border-gray-700 max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: `${config.color}20` }}>
              <config.icon size={18} style={{ color: config.color }} />
            </div>
            <h3 className="text-lg font-semibold text-white">{event.title || config.label}</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-1"><X size={22} /></button>
        </div>

        {/* Meta */}
        <div className="px-5 py-4 border-b border-gray-700/50">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">When</p>
              <p className="text-gray-200 text-sm font-medium">{formatTimestamp(event.timestamp)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">By</p>
              <p className="text-gray-200 text-sm font-medium">{event.performedByName || "System"}</p>
            </div>
          </div>
          {event.description && (
            <div className="mt-3">
              <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Details</p>
              <p className="text-gray-300 text-sm">{event.description}</p>
            </div>
          )}
        </div>

        {/* Changes Table */}
        {hasChanges && (
          <div className="p-5 border-b border-gray-700/50">
            <div className="border-t border-b border-gray-600 py-2 mb-4">
              <h4 className="text-center text-[#D19B15] font-semibold text-sm uppercase tracking-wider">Changes</h4>
            </div>
            <div className="overflow-hidden rounded-lg border border-gray-700">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#0f0f23]">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">Property</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Old Value</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-blue-400 uppercase tracking-wide">New Value</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(event.changes).map(([property, values]) => (
                    <tr key={property} className="border-t border-gray-700/50 hover:bg-[#0f0f23]/50">
                      <td className="py-3 px-4 text-sm text-gray-300 font-medium">{property}</td>
                      <td className="py-3 px-4 text-sm text-gray-500">{values.old || "—"}</td>
                      <td className="py-3 px-4 text-sm text-white font-medium">{values.new || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No changes fallback (only for non-hearing events) */}
        {!hasChanges && !isHearingEvent && (
          <div className="p-5 text-center text-gray-500 text-sm border-b border-gray-700/50">
            No detailed changes recorded for this event.
          </div>
        )}

        {/* Two-way notes for hearing_status events */}
        {isHearingEvent && hearingDocId && (
          <div className="p-5">
            <HearingNotes caseId={caseId} caseCollection={caseCollection} hearingDocId={hearingDocId} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Pending Hearing Card (shown when hearing date reached but not responded) ───

function PendingHearingCard({
  caseId,
  caseCollection,
  nextHearingDate,
  onStatusSubmitted,
  teamMembers = [],
  courtName,
  hearingNumber,
}) {
  const { globalUser, globalData } = useAuth();
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedNames, setSelectedNames] = useState([]);
  const [attendedByInput, setAttendedByInput] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [notesRefreshKey, setNotesRefreshKey] = useState(0);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const notesApiRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredMembers = teamMembers.filter(
    (m) =>
      m.name.toLowerCase().includes(attendedByInput.toLowerCase()) &&
      !selectedNames.includes(m.name)
  );

  const handleSelectMember = (name) => {
    if (!selectedNames.includes(name)) {
      setSelectedNames((prev) => [...prev, name]);
    }
    setAttendedByInput("");
    inputRef.current?.focus();
  };

  const handleRemoveName = (name) => {
    setSelectedNames((prev) => prev.filter((n) => n !== name));
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter" && attendedByInput.trim()) {
      e.preventDefault();
      const name = attendedByInput.trim();
      if (!selectedNames.includes(name)) {
        setSelectedNames((prev) => [...prev, name]);
      }
      setAttendedByInput("");
      setShowDropdown(false);
    }
    if (e.key === "Backspace" && !attendedByInput && selectedNames.length > 0) {
      setSelectedNames((prev) => prev.slice(0, -1));
    }
  };

  const attendedBy = selectedNames.join(", ");

  const hearingDocId = nextHearingDate ? nextHearingDate.replace(/\//g, "-") : null;

  const handleSubmitStatus = async () => {
    if (!selectedStatus || !hearingDocId) return;
    // Save hearing status doc
    const hearingRef = doc(db, caseCollection, caseId, "hearingNotes", hearingDocId);
    await setDoc(hearingRef, {
      status: selectedStatus,
      hearingDate: nextHearingDate,
      attendedBy: attendedBy.trim(),
      respondedBy: globalUser?.uid || "",
      respondedByName: globalData?.name || globalUser?.displayName || "",
      respondedAt: serverTimestamp(),
    }, { merge: true });

    // Create timeline event
    const statusLabel = STATUS_OPTIONS.find((s) => s.value === selectedStatus)?.label || selectedStatus;
    const changes = {
      "Hearing Date": { old: "—", new: nextHearingDate },
      "Status": { old: "Pending Response", new: statusLabel },
    };
    if (attendedBy.trim()) {
      changes["Attended By"] = { old: "—", new: attendedBy.trim() };
    }
    await addTimelineEvent(caseId, caseCollection, {
      type: "hearing_status",
      title: `Hearing: ${statusLabel}`,
      description: `Hearing on ${nextHearingDate} marked as "${statusLabel}"`,
      performedBy: globalUser?.uid || "",
      performedByName: globalData?.name || globalUser?.displayName || "",
      changes,
      metadata: { hearingDocId, hearingDate: nextHearingDate },
    });

    onStatusSubmitted();
  };

  const handleSaveHearing = async () => {
    if (!selectedStatus || submitting) return;
    setSubmitting(true);
    try {
      // Posts the pending note (if any) and then runs handleSubmitStatus.
      await notesApiRef.current?.submit();
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setSelectedStatus(null);
    setSelectedNames([]);
    setAttendedByInput("");
    setShowDropdown(false);
    notesApiRef.current?.reset();
  };

  const roleOf = (name) => teamMembers.find((m) => m.name === name)?.role || "";

  return (
    // Both cards are white and so is the tab panel behind them, so the gap does
    // the separating — hence the wider spacing than the mockup's grey page.
    <div className="mb-4 space-y-4">
      {/* ── Summary card ── */}
      <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-amber-50 ring-1 ring-inset ring-amber-100">
              <CalendarCheck size={24} className="text-[#C99F4A]" />
            </div>
            <div className="min-w-0">
              <h3 className="text-xl font-semibold text-slate-900">
                {hearingNumber ? `Hearing #${hearingNumber}` : "Hearing Date Status"}
              </h3>
              <p className="mt-1.5 flex items-center gap-2 text-sm text-slate-600">
                <CalendarCheck size={14} className="shrink-0 text-slate-400" />
                {formatHearingDateLong(nextHearingDate)}
              </p>
              {courtName && (
                <p className="mt-1 flex items-center gap-2 text-sm text-slate-600">
                  <MapPin size={14} className="shrink-0 text-slate-400" />
                  <span className="truncate">{courtName}</span>
                </p>
              )}
            </div>
          </div>

          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3.5 py-2 text-sm font-semibold text-[#B7841F] ring-1 ring-inset ring-amber-200">
            <Clock size={15} className="shrink-0" />
            Awaiting Response
          </span>
        </div>
      </div>

      {/* ── Details card ── */}
      <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50 ring-1 ring-inset ring-amber-100">
              <FileText size={16} className="text-[#C99F4A]" />
            </div>
            <h4 className="text-base font-semibold text-slate-900">Hearing Details</h4>
          </div>
          <button
            type="button"
            onClick={() => setNotesRefreshKey((k) => k + 1)}
            title="Refresh notes"
            className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            <RefreshCw size={16} />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {/* Left column — outcome + who attended */}
          <div>
            <p className="mb-2 text-sm font-medium text-slate-700">Outcome / How was the hearing?</p>
            <div className="flex divide-x divide-slate-200 overflow-hidden rounded-xl border border-slate-200">
              {STATUS_OPTIONS.map((opt) => {
                const isSelected = selectedStatus === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSelectedStatus(opt.value)}
                    className={`flex flex-1 items-center justify-center gap-2 px-3 py-3 text-sm transition-colors ${
                      isSelected
                        ? "bg-amber-50 text-slate-900 ring-1 ring-inset ring-[#C99F4A]"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
                        isSelected ? "border-[#C99F4A]" : "border-slate-300"
                      }`}
                    >
                      {isSelected && <span className="h-2 w-2 rounded-full bg-[#C99F4A]" />}
                    </span>
                    <span className={isSelected ? "font-semibold" : ""}>{opt.label}</span>
                  </button>
                );
              })}
            </div>

            <p className="mb-2 mt-4 text-sm font-medium text-slate-700">Attended By</p>
            <div className="relative w-full" ref={dropdownRef}>
              <div
                className="flex min-h-[52px] cursor-text flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 transition-colors focus-within:border-[#C99F4A] focus-within:ring-2 focus-within:ring-[#C99F4A]/25"
                onClick={() => inputRef.current?.focus()}
              >
                {selectedNames.map((name) => (
                  <span
                    key={name}
                    className="inline-flex items-center gap-2 rounded-lg bg-slate-50 py-1 pl-1 pr-2 ring-1 ring-inset ring-slate-200"
                  >
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-100 text-[11px] font-semibold text-[#B7841F]">
                      {initialsOf(name)}
                    </span>
                    <span className="leading-tight">
                      <span className="block text-sm font-medium text-slate-800">{name}</span>
                      {roleOf(name) && (
                        <span className="block text-[11px] text-slate-500">{roleOf(name)}</span>
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleRemoveName(name); }}
                      className="text-slate-400 transition-colors hover:text-slate-700"
                    >
                      <X size={13} />
                    </button>
                  </span>
                ))}
                <input
                  ref={inputRef}
                  type="text"
                  value={attendedByInput}
                  onChange={(e) => {
                    setAttendedByInput(e.target.value);
                    setShowDropdown(true);
                  }}
                  onFocus={() => setShowDropdown(true)}
                  onKeyDown={handleInputKeyDown}
                  placeholder={selectedNames.length === 0 ? "Type or select team member" : ""}
                  className="cd-chip-input min-w-[110px] flex-1 border-0 bg-transparent text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-0"
                />
                <ChevronDown size={16} className="ml-auto shrink-0 text-slate-400" />
              </div>
              {showDropdown && filteredMembers.length > 0 && (
                <div className="absolute z-50 mt-1 max-h-52 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-2xl ring-1 ring-black/5">
                  {filteredMembers.map((member) => (
                    <button
                      key={member.uid}
                      type="button"
                      onClick={() => handleSelectMember(member.name)}
                      className="flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-amber-50"
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-[11px] font-semibold text-[#B7841F]">
                        {initialsOf(member.name)}
                      </span>
                      <span className="min-w-0 leading-tight">
                        <span className="block truncate text-sm font-medium text-slate-800">{member.name}</span>
                        {member.role && (
                          <span className="block truncate text-[11px] text-slate-500">{member.role}</span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right column — notes */}
          <div>
            <p className="mb-2 text-sm font-medium text-slate-700">Hearing Notes</p>
            <HearingNotes
              caseId={caseId}
              caseCollection={caseCollection}
              hearingDocId={hearingDocId}
              onAfterSave={handleSubmitStatus}
              saveDisabled={!selectedStatus}
              hideHeader
              externalComposer
              apiRef={notesApiRef}
              refreshKey={notesRefreshKey}
            />
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-end gap-3">
          <button
            type="button"
            onClick={handleCancel}
            className="rounded-xl border border-slate-200 bg-white px-6 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSaveHearing}
            disabled={!selectedStatus || submitting}
            className="inline-flex items-center gap-2 rounded-xl bg-yellow-500 px-6 py-2.5 text-sm font-semibold text-gray-900 transition-colors hover:bg-yellow-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-900 border-t-transparent" />
            ) : (
              <Send size={16} />
            )}
            Save Hearing
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Unified Hearing Status Modal (form when unfilled, details when filled) ───

function HearingStatusModal({ hearingDate, caseId, caseCollection, teamMembers = [], onClose, onAfterChange }) {
  const { globalUser, globalData } = useAuth();
  const hearingDocId = hearingDate ? hearingDate.replace(/\//g, "-") : null;

  const [loading, setLoading] = useState(true);
  const [hearingDoc, setHearingDoc] = useState(null);

  // Form state (used only in unfilled mode)
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedNames, setSelectedNames] = useState([]);
  const [attendedByInput, setAttendedByInput] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const fetchHearing = async () => {
    if (!hearingDocId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const snap = await getDoc(doc(db, caseCollection, caseId, "hearingNotes", hearingDocId));
      setHearingDoc(snap.exists() ? snap.data() : null);
    } catch (err) {
      console.error("Error loading hearing doc:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHearing();
  }, [hearingDocId, caseId, caseCollection]);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isFilled = !!hearingDoc?.status;
  const statusMeta = STATUS_OPTIONS.find((s) => s.value === hearingDoc?.status);

  const filteredMembers = teamMembers.filter(
    (m) => m.name.toLowerCase().includes(attendedByInput.toLowerCase()) && !selectedNames.includes(m.name)
  );

  const handleSelectMember = (name) => {
    if (!selectedNames.includes(name)) setSelectedNames((p) => [...p, name]);
    setAttendedByInput("");
    inputRef.current?.focus();
  };
  const handleRemoveName = (name) => setSelectedNames((p) => p.filter((n) => n !== name));
  const handleInputKeyDown = (e) => {
    if (e.key === "Enter" && attendedByInput.trim()) {
      e.preventDefault();
      const name = attendedByInput.trim();
      if (!selectedNames.includes(name)) setSelectedNames((p) => [...p, name]);
      setAttendedByInput("");
      setShowDropdown(false);
    }
    if (e.key === "Backspace" && !attendedByInput && selectedNames.length > 0) {
      setSelectedNames((p) => p.slice(0, -1));
    }
  };

  const attendedBy = selectedNames.join(", ");

  const handleSubmit = async () => {
    if (!selectedStatus || !hearingDocId || submitting) return;
    setSubmitting(true);
    try {
      const hearingRef = doc(db, caseCollection, caseId, "hearingNotes", hearingDocId);
      await setDoc(
        hearingRef,
        {
          status: selectedStatus,
          hearingDate,
          attendedBy: attendedBy.trim(),
          respondedBy: globalUser?.uid || "",
          respondedByName: globalData?.name || globalUser?.displayName || "",
          respondedAt: serverTimestamp(),
        },
        { merge: true }
      );

      const statusLabel = STATUS_OPTIONS.find((s) => s.value === selectedStatus)?.label || selectedStatus;
      const changes = {
        "Hearing Date": { old: "—", new: hearingDate },
        Status: { old: "Not Filled", new: statusLabel },
      };
      if (attendedBy.trim()) changes["Attended By"] = { old: "—", new: attendedBy.trim() };

      await addTimelineEvent(caseId, caseCollection, {
        type: "hearing_status",
        title: `Hearing: ${statusLabel}`,
        description: `Hearing on ${hearingDate} marked as "${statusLabel}"`,
        performedBy: globalUser?.uid || "",
        performedByName: globalData?.name || globalUser?.displayName || "",
        changes,
        metadata: { hearingDocId, hearingDate },
      });

      await fetchHearing();
      onAfterChange?.();
    } catch (err) {
      console.error("Error submitting hearing status:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const headerColor = isFilled ? statusMeta?.color || "#D19B15" : "#f59e0b";
  const HeaderIcon = isFilled && statusMeta ? statusMeta.icon : AlertCircle;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[1100] p-4 pt-20">
      <div className="bg-[#1a1a2e] rounded-xl max-w-2xl w-full shadow-2xl border border-gray-700 max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${headerColor}20` }}
            >
              <HeaderIcon size={18} style={{ color: headerColor }} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                {isFilled ? `Hearing: ${statusMeta?.label || hearingDoc?.status}` : "Hearing Status — Not Filled"}
              </h3>
              <p className="text-blue-300 text-xs font-medium mt-0.5">{hearingDate}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-1">
            <X size={22} />
          </button>
        </div>

        {loading && (
          <div className="p-8 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-gray-600 border-t-blue-400 rounded-full animate-spin" />
          </div>
        )}

        {/* Unfilled — show form */}
        {!loading && !isFilled && (
          <>
            <div className="p-5 border-b border-gray-700/50">
              <p className="text-gray-400 text-xs mb-2 uppercase tracking-wide font-medium">How was the hearing?</p>
              <div className="flex gap-2 flex-wrap mb-4">
                {STATUS_OPTIONS.map((opt) => {
                  const isSelected = selectedStatus === opt.value;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => setSelectedStatus(opt.value)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        isSelected
                          ? "border-gray-400 bg-gray-500/30 text-white"
                          : "border-white/40 text-white hover:border-white/60"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>

              <p className="text-gray-400 text-xs mb-1.5 uppercase tracking-wide font-medium">Attended By</p>
              <div className="relative w-full max-w-xs" ref={dropdownRef}>
                <div
                  className="flex flex-wrap items-center gap-1.5 bg-white border border-gray-300 rounded-lg px-2 py-1.5 min-h-[38px] cursor-text focus-within:border-blue-500 transition-colors"
                  onClick={() => inputRef.current?.focus()}
                >
                  {selectedNames.map((name) => (
                    <span
                      key={name}
                      className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs font-medium px-2 py-1 rounded-md border border-blue-200"
                    >
                      {name}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveName(name);
                        }}
                        className="text-blue-400 hover:text-white transition-colors"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                  <input
                    ref={inputRef}
                    type="text"
                    value={attendedByInput}
                    onChange={(e) => {
                      setAttendedByInput(e.target.value);
                      setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    onKeyDown={handleInputKeyDown}
                    placeholder={selectedNames.length === 0 ? "Type or select team member" : ""}
                    className="cd-chip-input flex-1 min-w-[80px] bg-transparent text-sm text-gray-800 placeholder-gray-400 focus:outline-none"
                  />
                </div>
                {showDropdown && filteredMembers.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full max-h-40 overflow-y-auto bg-white border border-gray-300 rounded-xl shadow-2xl ring-1 ring-black/5">
                    {filteredMembers.map((member, idx) => (
                      <React.Fragment key={member.uid}>
                        {idx > 0 && <div className="border-t border-blue-900/30" />}
                        <button
                          type="button"
                          onClick={() => handleSelectMember(member.name)}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 transition-colors"
                        >
                          {member.name}
                        </button>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={handleSubmit}
                disabled={!selectedStatus || submitting}
                className="w-full mt-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Submit Hearing Status"
                )}
              </button>
            </div>

            <div className="p-5">
              <HearingNotes caseId={caseId} caseCollection={caseCollection} hearingDocId={hearingDocId} />
            </div>
          </>
        )}

        {/* Filled — show details */}
        {!loading && isFilled && (
          <>
            <div className="px-5 py-4 border-b border-gray-700/50">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Status</p>
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                      statusMeta?.bg || ""
                    }`}
                  >
                    {statusMeta?.icon && <statusMeta.icon size={12} />}
                    {statusMeta?.label || hearingDoc?.status}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Attended By</p>
                  <p className="text-gray-200 text-sm font-medium">{hearingDoc?.attendedBy || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Responded By</p>
                  <p className="text-gray-200 text-sm font-medium">{hearingDoc?.respondedByName || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Responded At</p>
                  <p className="text-gray-200 text-sm font-medium">
                    {hearingDoc?.respondedAt ? formatTimestamp(hearingDoc.respondedAt) : "—"}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-5">
              <HearingNotes caseId={caseId} caseCollection={caseCollection} hearingDocId={hearingDocId} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main Timeline Component ───

function CaseTimeline({ caseId, caseCollection, caseData, maxVisible, isClientUser = false }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [hearingResponded, setHearingResponded] = useState(null); // null=loading, true/false
  const [refreshKey, setRefreshKey] = useState(0);
  const [showAllEventsModal, setShowAllEventsModal] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [unfilledPastHearings, setUnfilledPastHearings] = useState([]);

  const nextHearingDate = caseData?.nextHearingDate;
  const hearingDocId = nextHearingDate ? nextHearingDate.replace(/\//g, "-") : null;
  const showPendingCard = nextHearingDate && isHearingDateReached(nextHearingDate) && hearingResponded === false && !isClientUser;

  // Check if hearing has been responded to
  useEffect(() => {
    if (!caseId || !caseCollection || !hearingDocId) {
      setHearingResponded(true); // no hearing = don't show card
      return;
    }
    const checkHearingStatus = async () => {
      try {
        const hearingRef = doc(db, caseCollection, caseId, "hearingNotes", hearingDocId);
        const snap = await getDoc(hearingRef);
        if (snap.exists() && snap.data().status) {
          setHearingResponded(true);
        } else {
          setHearingResponded(false);
        }
      } catch (err) {
        console.error("Error checking hearing status:", err);
        setHearingResponded(true); // fallback: hide card on error
      }
    };
    checkHearingStatus();
  }, [caseId, caseCollection, hearingDocId, refreshKey]);

  // Fetch timeline events
  useEffect(() => {
    if (!caseId || !caseCollection) return;
    const fetchTimeline = async () => {
      setLoading(true);
      try {
        const timelineRef = collection(db, caseCollection, caseId, "timeline");
        const q = query(timelineRef, orderBy("timestamp", "desc"));
        const snapshot = await getDocs(q);
        setEvents(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error("Error fetching timeline:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTimeline();
  }, [caseId, caseCollection, refreshKey]);

  // Fetch team members for the case owner
  useEffect(() => {
    const fetchTeamMembers = async () => {
      const ownerId = caseData?.owner;
      if (!ownerId) return;
      try {
        const teamsQuery = query(
          collection(db, "teams"),
          where("ownerId", "==", ownerId)
        );
        const teamsSnapshot = await getDocs(teamsQuery);
        const memberUids = new Set();
        teamsSnapshot.docs.forEach((d) => {
          const members = d.data().members || [];
          members.forEach((uid) => memberUids.add(uid));
        });
        // Also add the owner
        memberUids.add(ownerId);
        // Fetch names for all member uids
        const membersList = [];
        await Promise.all(
          Array.from(memberUids).map(async (uid) => {
            try {
              const userDoc = await getDoc(doc(db, "lawyers", uid));
              if (userDoc.exists()) {
                const u = userDoc.data();
                membersList.push({
                  uid,
                  name: u.name || "Unknown",
                  // Shown under the name in the Attended By picker; blank for
                  // profiles that never filled it in.
                  role: u.userCategory || u.designation || u.degree || "",
                });
              }
            } catch (err) {
              console.error("Error fetching team member:", err);
            }
          })
        );
        setTeamMembers(membersList);
      } catch (err) {
        console.error("Error fetching team members:", err);
      }
    };
    fetchTeamMembers();
  }, [caseData?.owner]);

  const handleStatusSubmitted = () => {
    setHearingResponded(true);
    setRefreshKey((k) => k + 1); // re-fetch timeline to show new event
  };

  // ─── Backfill: detect past hearings that have no status filled ───
  const candidatePastDates = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dates = new Set();
    (caseData?.caseHistory || []).forEach((h) => {
      if (!h?.hearingDate) return;
      const d = parseDDMMYYYY(h.hearingDate);
      if (d && d < today) dates.add(h.hearingDate);
    });
    if (caseData?.previousHearingDate) {
      const d = parseDDMMYYYY(caseData.previousHearingDate);
      if (d && d < today) dates.add(caseData.previousHearingDate);
    }
    return Array.from(dates);
  }, [caseData?.caseHistory, caseData?.previousHearingDate]);

  useEffect(() => {
    if (!caseId || !caseCollection || candidatePastDates.length === 0) {
      setUnfilledPastHearings([]);
      return;
    }
    let cancelled = false;
    const check = async () => {
      const results = await Promise.all(
        candidatePastDates.map(async (date) => {
          const docId = date.replace(/\//g, "-");
          try {
            const snap = await getDoc(doc(db, caseCollection, caseId, "hearingNotes", docId));
            if (snap.exists() && snap.data().status) return null;
            return date;
          } catch (err) {
            console.error("Error checking hearing status for", date, err);
            return null;
          }
        })
      );
      if (!cancelled) setUnfilledPastHearings(results.filter(Boolean));
    };
    check();
    return () => {
      cancelled = true;
    };
  }, [caseId, caseCollection, candidatePastDates.join("|"), refreshKey]);

  const syntheticEvents = useMemo(() => {
    return unfilledPastHearings.map((hd) => {
      const dt = parseDDMMYYYY(hd);
      return {
        id: `synth-nf-${hd.replace(/\//g, "-")}`,
        type: "hearing_not_filled",
        title: "Hearing Status Not Filled",
        description: `Hearing on ${hd} — status unrecorded`,
        timestamp: dt || new Date(),
        hearingDate: hd,
        isSynthetic: true,
      };
    });
  }, [unfilledPastHearings]);

  const mergedEvents = useMemo(() => {
    const ms = (ts) => {
      if (!ts) return 0;
      if (typeof ts.toMillis === "function") return ts.toMillis();
      if (typeof ts.toDate === "function") return ts.toDate().getTime();
      return new Date(ts).getTime();
    };
    return [...events, ...syntheticEvents].sort((a, b) => ms(b.timestamp) - ms(a.timestamp));
  }, [events, syntheticEvents]);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex gap-4">
            <div className="w-10 h-10 bg-gray-700 rounded-full flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-700 rounded w-1/3" />
              <div className="h-3 bg-gray-700 rounded w-2/3" />
              <div className="h-3 bg-gray-700 rounded w-1/4" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  const hasMoreEvents = maxVisible && mergedEvents.length > maxVisible;

  const renderTimelineEvents = (eventsToRender, showStartIndicator = true) => (
    <div className="relative">
      <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-700" />

      <div className="space-y-1">
        {eventsToRender.map((event, index) => {
          const config = EVENT_CONFIG[event.type] || EVENT_CONFIG.unknown;
          const IconComponent = config.icon;
          const isFirst = index === 0;
          const hasChanges = event.changes && Object.keys(event.changes).length > 0;
          const isHearingEvent = event.type === "hearing_status" || event.type === "hearing_not_filled";
          const isNotFilled = event.type === "hearing_not_filled";

          return (
            <div
              key={event.id}
              onClick={() => setSelectedEvent(event)}
              className="relative flex gap-4 cursor-pointer hover:bg-gray-50 rounded-lg p-2 -ml-2 transition-colors group"
            >
              <div
                className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                  isFirst ? "border-green-500" : "border-transparent"
                }`}
                style={{ backgroundColor: `${config.color}20` }}
              >
                <IconComponent size={18} style={{ color: config.color }} />
              </div>

              <div className="flex-1 min-w-0 py-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium text-sm truncate">{event.title || config.label}</p>
                      {isFirst && (
                        <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-semibold bg-green-500/20 text-green-400 rounded">Latest</span>
                      )}
                    </div>
                    {event.description && (
                      <p className="text-gray-400 text-sm mt-0.5 truncate">{event.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-1">
                      {event.performedByName && (
                        <p className="text-gray-500 text-xs">by {event.performedByName}</p>
                      )}
                      {(hasChanges || isHearingEvent) && (
                        <span className="text-[#D19B15] text-xs flex items-center gap-0.5">
                          {isNotFilled ? (
                            <><AlertCircle size={11} /> Fill hearing status</>
                          ) : isHearingEvent ? (
                            <><MessageSquare size={11} /> View details & notes</>
                          ) : (
                            <><ChevronRight size={12} /> View changes</>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-gray-500 text-xs whitespace-nowrap flex-shrink-0 mt-0.5">
                    {getRelativeTime(event.timestamp)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Start indicator — only when showing all events */}
      {showStartIndicator && (
        <div className="relative flex gap-4 pl-2 pt-2 -ml-2">
          <div className="relative z-10 flex-shrink-0 w-10 h-10 rounded-full bg-gray-800 border-2 border-gray-600 flex items-center justify-center">
            <div className="w-2 h-2 bg-gray-500 rounded-full" />
          </div>
          <p className="text-sm text-gray-500 self-center">Start</p>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Pending Hearing Card — only shows from hearing date until responded */}
      {showPendingCard && (
        <PendingHearingCard
          caseId={caseId}
          caseCollection={caseCollection}
          nextHearingDate={nextHearingDate}
          onStatusSubmitted={handleStatusSubmitted}
          teamMembers={teamMembers}
          courtName={caseData?.court}
          // Every past hearing date on the case, plus the one being filled now.
          hearingNumber={candidatePastDates.length + 1}
        />
      )}

      {/* View More header — top right like case history */}
      {hasMoreEvents && (
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[#C99F4A] font-semibold text-lg">Timeline</h3>
          <button
            onClick={() => setShowAllEventsModal(true)}
            className="text-[#C99F4A] hover:text-[#D19B15] text-sm font-medium"
          >
            View All ({mergedEvents.length})
          </button>
        </div>
      )}

      {/* Scrollable timeline container — fixed height */}
      <div className="max-h-[420px] overflow-y-auto pr-1">
        {mergedEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Clock size={48} className="mb-4 opacity-50" />
            <p className="text-lg font-medium">No activity yet</p>
            <p className="text-sm mt-1">Timeline events will appear here as actions are performed on this case.</p>
          </div>
        ) : (
          renderTimelineEvents(mergedEvents, true)
        )}
      </div>

      {/* All Events Modal */}
      {showAllEventsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[1100] p-4 pt-20">
          <div className="bg-[#1a1a2e] rounded-xl max-w-2xl w-full shadow-2xl border border-gray-700 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#D19B15]/15 flex items-center justify-center">
                  <Clock size={18} className="text-[#D19B15]" />
                </div>
                <h3 className="text-lg font-semibold text-white">Full Timeline ({mergedEvents.length} events)</h3>
              </div>
              <button onClick={() => setShowAllEventsModal(false)} className="text-gray-400 hover:text-white transition-colors p-1">
                <X size={22} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {renderTimelineEvents(mergedEvents, true)}
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedEvent && (
        (selectedEvent.type === "hearing_status" || selectedEvent.type === "hearing_not_filled") ? (
          <HearingStatusModal
            hearingDate={
              selectedEvent.hearingDate ||
              (selectedEvent.metadata?.hearingDocId
                ? selectedEvent.metadata.hearingDocId.replace(/-/g, "/")
                : selectedEvent.changes?.["Hearing Date"]?.new) ||
              ""
            }
            caseId={caseId}
            caseCollection={caseCollection}
            teamMembers={teamMembers}
            onClose={() => setSelectedEvent(null)}
            onAfterChange={() => setRefreshKey((k) => k + 1)}
          />
        ) : (
          <TimelineDetailModal
            event={selectedEvent}
            onClose={() => setSelectedEvent(null)}
            caseId={caseId}
            caseCollection={caseCollection}
          />
        )
      )}
    </>
  );
}

export default CaseTimeline;
